/**
 * Position dots for the craft-steps row on phones.
 *
 * The row itself is pure CSS scroll-snap (see design-language.css). This only
 * adds the indicator: the generic `section` type has no slot to put one in from
 * the editor, so the markup is built here and inserted after the track.
 *
 * Does nothing at all unless that one section is on the page.
 */
const SECTION = '[id*="section_PyEYKB"]';
const TRACK = '.section-content-wrapper.layout-panel-flex';

/**
 * Brings a card to the track's inline start.
 *
 * scrollIntoView was the obvious call and the wrong one: it walks every
 * scrollable ancestor, so a press moved the page as well as the track, and two
 * presses in quick succession cancelled each other -- the row landed on the
 * card from the press before. This touches only the track, and works out the
 * distance from where things actually are, which keeps it correct in RTL
 * without reasoning about the sign of scrollLeft.
 *
 * @param {HTMLElement} track
 * @param {Element} card
 */
function scrollTo(track, card) {
  const styles = getComputedStyle(track);
  const rtl = styles.direction === 'rtl';
  const pad = parseFloat(styles.paddingInlineStart) || 0;
  const trackBox = track.getBoundingClientRect();
  const cardBox = card.getBoundingClientRect();
  const delta = rtl ? cardBox.right - (trackBox.right - pad) : cardBox.left - (trackBox.left + pad);

  track.scrollBy({ left: delta, behavior: 'smooth' });
}

function init() {
  const section = document.querySelector(SECTION);
  if (!section) return;

  const track = section.querySelector(TRACK);
  if (!track) return;

  const cards = Array.from(track.children);
  // One card cannot be navigated, and an existing nav means we already ran.
  if (cards.length < 2 || track.parentElement?.querySelector('.craft-dots')) return;

  const nav = document.createElement('nav');
  nav.className = 'craft-dots';
  nav.setAttribute('aria-label', 'שלבי המלאכה');

  const dots = cards.map((card, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'craft-dots__dot';
    // The step's own heading, so the control is announced as "צביעה" rather
    // than "שלב 3".
    const label = (card.textContent || '').trim().split('\n')[0].trim();
    dot.setAttribute('aria-label', label || `שלב ${index + 1}`);
    dot.addEventListener('click', () => scrollTo(track, card));
    nav.appendChild(dot);
    return dot;
  });

  track.insertAdjacentElement('afterend', nav);

  const setCurrent = (index) => {
    dots.forEach((dot, i) => {
      dot.toggleAttribute('data-current', i === index);
      // aria-current carries the state to assistive tech; the attribute above
      // is only what the stylesheet hangs on.
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  };

  setCurrent(0);

  /* 0.6 separates a snapped card from the slice of the next one: snapped, a
     card is fully inside the track; the peek is barely a quarter of itself. */
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const index = cards.indexOf(entry.target);
        if (index !== -1) setCurrent(index);
      }
    },
    { root: track, threshold: 0.6 }
  );

  cards.forEach((card) => observer.observe(card));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
